// Step 4 of the missing-item flow: say what happened, and what to do now.
//
// The resolution used to arrive as a toast over the pick screen ("Item found at
// NPS1-A-3") while the hero had already moved on to some other line. Pickers
// read that as "the system has dealt with it" and walked on — the whole point
// of the count (go and get it from the other box / it's flagged for dispatch)
// was lost in a message that disappeared after three seconds.
//
// So each outcome gets a full screen with one instruction on it, and the picker
// dismisses it themselves. Scanning is blocked underneath while it's up, so a
// stray trigger pull can't skip past the instruction (or be eaten by it).
import { CheckCircle2, MapPin, PackageX, ArrowRight } from "lucide-react";
import { useTranslation } from "../../i18n";

export type MissingResolutionKind = "found-here" | "found-elsewhere" | "not-found";

export interface MissingResolution {
    kind: MissingResolutionKind;
    sku: string;
    /** The location that was counted. */
    location: string;
    foundQty: number;
    requiredQty: number;
    /**
     * Units the count found that stay here on lines the picker now picks
     * before moving on — a partial find on a box whose other lines were
     * re-slotted or flagged. 0 / absent when nothing stays.
     */
    keptHereQty?: number;
    /** found-elsewhere: where the stock is coming from instead. */
    redirectLocation?: string | null;
    /** found-elsewhere: true when that box is the picker's very next stop. */
    redirectIsNextStop?: boolean;
    /** not-found: how many orders / units were pre-marked missing for dispatch. */
    flaggedOrders?: number;
    flaggedQty?: number;
    /** not-found: orders left with nothing to send, flagged for leader review. */
    ordersFlaggedForReview?: number;
    /** True when the wave finished as a result — Continue leaves the wave. */
    waveComplete?: boolean;
}

interface Props {
    resolution: MissingResolution;
    busy?: boolean;
    onContinue: () => void;
}

const MissingResolutionCard = ({ resolution, busy, onContinue }: Props) => {
    const { t } = useTranslation();
    const r = resolution;

    const icon = r.kind === "found-here"
        ? <CheckCircle2 size={96} strokeWidth={1.4} />
        : r.kind === "found-elsewhere"
            ? <MapPin size={96} strokeWidth={1.4} />
            : <PackageX size={96} strokeWidth={1.4} />;

    const headline = r.kind === "found-here"
        ? t("picking_missing_res_found_title")
        : r.kind === "found-elsewhere"
            ? t("picking_missing_res_elsewhere_title")
            : t("picking_missing_res_notfound_title");

    // The instruction. One sentence, in the imperative where there is
    // something to do, because this is the only place it gets said.
    const detail = r.kind === "found-here"
        ? t("picking_missing_res_found_body", {
            count: String(Math.max(1, r.requiredQty)),
            sku: r.sku,
            location: r.location,
        })
        : r.kind === "found-elsewhere"
            ? (r.redirectIsNextStop
                ? t("picking_missing_res_elsewhere_next", { location: r.redirectLocation ?? "" })
                : t("picking_missing_res_elsewhere_later", { location: r.redirectLocation ?? "" }))
            : t("picking_missing_res_notfound_body", { sku: r.sku });

    // Secondary line — the consequence elsewhere in the business, so the
    // picker knows the shortfall is already handled and does not go hunting.
    // A box whose lines split (some re-slotted, the rest short anywhere)
    // arrives as found-elsewhere WITH a flagged quantity, and the shortfall
    // is said there too rather than claiming nothing is short.
    const footnote = r.kind === "not-found"
        ? (r.flaggedQty
            ? t("picking_missing_res_notfound_dispatch", {
                count: String(r.flaggedQty),
                orders: String(r.flaggedOrders ?? 0),
            })
            : t("picking_missing_res_notfound_dispatch_none"))
        : r.kind === "found-elsewhere"
            ? (r.flaggedQty
                ? t("picking_missing_res_notfound_dispatch", {
                    count: String(r.flaggedQty),
                    orders: String(r.flaggedOrders ?? 0),
                })
                : t("picking_missing_res_elsewhere_note"))
            : null;

    // Part of the count turned up. Those units stay here on lines the picker
    // picks before moving on — said separately, because the headline is about
    // the units that did NOT, and a picker who walks off to the other box
    // leaves these behind.
    const keptNote = r.kind !== "found-here" && (r.keptHereQty ?? 0) > 0
        ? t("picking_missing_res_kept_here", {
            count: String(r.keptHereQty),
            sku: r.sku,
        })
        : null;

    // An order that lost EVERY unit can't be packed at all, so it leaves the
    // dispatch queue entirely and goes to a leader instead. Said separately
    // from the shortfall line because it is a different outcome, and the
    // picker is often the person who gets asked about it afterwards.
    const reviewNote = r.kind === "not-found" && (r.ordersFlaggedForReview ?? 0) > 0
        ? t("picking_missing_res_notfound_review", {
            count: String(r.ordersFlaggedForReview),
        })
        : null;

    return (
        <div className={`picking-missing-resolution picking-missing-resolution--${r.kind}`}>
            <div className="picking-missing-resolution-icon">{icon}</div>
            <h2 className="picking-missing-resolution-title">{headline}</h2>
            <p className="picking-missing-resolution-detail">{detail}</p>
            {keptNote && (
                <p className="picking-missing-resolution-note picking-missing-resolution-note--kept">
                    {keptNote}
                </p>
            )}
            {footnote && (
                <p className="picking-missing-resolution-note">{footnote}</p>
            )}
            {reviewNote && (
                <p className="picking-missing-resolution-note picking-missing-resolution-note--review">
                    {reviewNote}
                </p>
            )}
            <button
                type="button"
                className="btn btn-primary btn-lg picking-missing-resolution-btn"
                onClick={onContinue}
                disabled={busy}
            >
                {r.waveComplete
                    ? t("picking_missing_res_finish_wave")
                    : t("picking_missing_res_continue")}
                <ArrowRight size={20} />
            </button>
        </div>
    );
};

export default MissingResolutionCard;
